import * as React from "react"
import { useReadLegalEntityMetadataQuery } from "@digits-graphql/frontend/graphql-bearer"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"

export function useTransactionTimestamps(skip?: boolean) {
  const viewKey = useViewVersion()
  const { isSharingContextActive, currentLegalEntity, hasAccessToAspect } =
    useSession<FrontendSession>()
  const allowFutureDates = hasAccessToAspect?.(AspectCode.FutureDatedTransactions) ?? false

  const { data, loading, error } = useReadLegalEntityMetadataQuery({
    variables: {
      viewKey,
    },
    skip:
      !viewKey ||
      !viewKey.legalEntityId ||
      !viewKey.viewVersion ||
      !currentLegalEntity?.hasDashboardAccess() ||
      isSharingContextActive ||
      skip,
  })

  return React.useMemo(() => {
    const firstTransactionTimestamp = data?.readLegalEntityMetadata?.firstTransactionTimestamp || 0
    const latestTransactionTimestamp =
      data?.readLegalEntityMetadata?.latestTransactionTimestamp || 0
    const firstDate = dayjs.unix(firstTransactionTimestamp)
    const latestDate = dayjs.unix(latestTransactionTimestamp)

    const todayEnd = dayjs.utc().endOf("day").unix()
    let maxNavigableTimestamp: number
    if (allowFutureDates && latestTransactionTimestamp) {
      maxNavigableTimestamp = Math.max(latestTransactionTimestamp, todayEnd)
    } else if (allowFutureDates && loading) {
      // While metadata is loading, use a generous upper bound to avoid prematurely
      // capping future-dated URLs before the real boundary is known.
      maxNavigableTimestamp = dayjs.utc().add(10, "year").endOf("year").unix()
    } else {
      maxNavigableTimestamp = todayEnd
    }
    const maxNavigableYear = dayjs.utc(maxNavigableTimestamp * 1000).year()

    return {
      loading,
      error,
      firstTransactionTimestamp,
      firstTransactionMonth: firstDate.month(),
      firstTransactionYear: firstDate.year(),
      latestTransactionTimestamp,
      latestTransactionMonth: latestDate.month(),
      latestTransactionYear: latestDate.year(),
      maxNavigableTimestamp,
      maxNavigableYear,
    }
  }, [
    data?.readLegalEntityMetadata?.firstTransactionTimestamp,
    data?.readLegalEntityMetadata?.latestTransactionTimestamp,
    error,
    loading,
    allowFutureDates,
  ])
}
